import ApplicationModel, { attr } from '../ApplicationModel';
import EstimateTypeFilter, {
  type EstimateTypeFilterValue,
} from '../filters/EstimateTypeFilter';
import type ItemFilter from '../filters/ItemFilter';
import IterationFilter from '../filters/IterationFilter';
import RecordTypeFilter from '../filters/RecordTypeFilter';
import type { UnitType } from '../lib/Estimate';
import { nameMap } from '../lib/nameMap';
import { model } from '../modelBuilder';
import type Iteration from './Iteration';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import { HasIterations } from './concerns/HasIterations';
import { type BookmarksIterationBurnupAttributes } from './generated/BookmarksIterationBurnupAttributes';

type BurnupFilters = {
  iterations: IterationFilter;
  recordType: RecordTypeFilter;
  estimationType: EstimateTypeFilter;
};

export type BurnupFilterScope = keyof BurnupFilters;

interface BookmarksIterationBurnup
  extends BookmarksIterationBurnupAttributes,
    HasBookmarkFilters<BurnupFilters>,
    HasIterations {
  filters: {
    iterations?: RecordId[];
    recordType?: string[];
    estimationType?: EstimateTypeFilterValue[];
  };
  local: {
    filterObjects?: Record<string, ItemFilter<string | RecordId>>;
  };
}
@model
@HasBookmarkFilters
@HasIterations
class BookmarksIterationBurnup extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksIterationBurnup' = 'BookmarksIterationBurnup';
  get typename(): 'BookmarksIterationBurnup' {
    return 'BookmarksIterationBurnup';
  }

  static internalTypename: 'Bookmarks::IterationBurnup' =
    'Bookmarks::IterationBurnup';
  get internalTypename(): 'Bookmarks::IterationBurnup' {
    return 'Bookmarks::IterationBurnup';
  }

  static bookmarkName = 'Bookmarks::IterationBurnup';

  static inputType = 'IterationBurnupAttributes!';

  static fields = {
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
    recentIterationsBurnupStats: attr.belongsTo({
      readable: true,
      typename: 'BookmarksIterationBurnupStats',
    }),
  };
  // End auto-generated

  async createFilters(): Promise<BurnupFilters> {
    const iterations = await this.availableIterations();

    const iterationFilter = new IterationFilter(
      this.filters.iterations || [],
      (iterations ?? []).map(nameMap)
    );

    let defaultEstimationType: EstimateTypeFilterValue = 'count';

    const iteration = await this.iteration(this.filters.iterations?.[0]);

    if (iteration?.defaultEstimationType === 'original') {
      defaultEstimationType = 'originalEstimate';
    }

    return {
      iterations: iterationFilter,
      recordType: new RecordTypeFilter({
        initialItems:
          typeof this.filters.recordType === 'string'
            ? // Convert legacy recordType shape ^
              [this.filters.recordType]
            : Array.isArray(this.filters.recordType)
            ? this.filters.recordType
            : [],
        recordTypes: ['Feature', 'Requirement'],
      }),
      estimationType: new EstimateTypeFilter({
        initialItems: this.filters.estimationType || [defaultEstimationType],
        capacityUnits: iteration?.originalEstimate?.units,
      }),
    };
  }

  /**
   * Fetch events for the selected iteration
   *
   * @param filters Any filters to apply to the raw data fetch.
   */
  async recordEvents(filters: Record<string, any> = {}) {
    const iteration = await this.iteration();
    return iteration?.eventsRaw(filters);
  }

  /**
   * Load the selected iteration using the available iterations
   */
  async iteration(iterationId?: string): Promise<Iteration | null> {
    const iterations = await this.availableIterations();
    if (!iterations) return null;

    iterationId = iterationId ?? this.getFilter('iterations')?.single();
    if (!iterationId) return iterations[0];

    return iterations.find(i => i.id === iterationId) || iterations[0];
  }

  /**
   * Load the selected unit
   */
  async units(): Promise<UnitType> {
    const units = this.getFilter('estimationType')?.single();

    if (units === 'count') {
      return 'COUNT';
    } else {
      const iteration = await this.iteration();
      return iteration?.originalEstimate?.units ?? 'POINTS';
    }
  }
}

export default BookmarksIterationBurnup;
